import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  getCellar,
  getCellarStats,
  addToCellar,
  updateCellarItem,
  deleteCellarItem,
  getWines,
} from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Wine,
  Package,
  DollarSign,
  AlarmClock,
  Layers,
  Plus,
  Trash2,
  Edit,
  MapPin,
  Grape,
} from 'lucide-react';

const StatCard = ({ icon: Icon, label, value, accent = '#d4af37' }) => (
  <div
    className="p-6 bg-stone-900/30 border border-white/5"
    data-testid={`cellar-stat-${label.toLowerCase().replace(/\W+/g, '-')}`}
  >
    <div className="flex items-center gap-3 mb-2">
      <div
        className="w-10 h-10 flex items-center justify-center border"
        style={{ borderColor: `${accent}33`, color: accent }}
      >
        <Icon className="w-5 h-5" />
      </div>
      <span className="text-xs uppercase tracking-widest text-stone-500">{label}</span>
    </div>
    <p className="text-3xl font-serif text-stone-50">{value}</p>
  </div>
);

const emptyForm = {
  wine_id: '',
  quantity: 1,
  purchase_date: '',
  purchase_price: '',
  drink_by_year: '',
  storage_location: '',
  notes: '',
};

const Cellar = () => {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [wines, setWines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null); // cellar item being edited
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, authLoading]);

  const load = async () => {
    try {
      const [i, s, w] = await Promise.all([
        getCellar(),
        getCellarStats(),
        getWines({ limit: 200 }),
      ]);
      setItems(i);
      setStats(s);
      setWines(w);
    } catch (e) {
      toast.error('Failed to load cellar');
    } finally {
      setLoading(false);
    }
  };

  const openAdd = () => {
    setEditing(null);
    setForm(emptyForm);
    setShowForm(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({
      wine_id: item.wine_id,
      quantity: item.quantity,
      purchase_date: item.purchase_date || '',
      purchase_price: item.purchase_price ?? '',
      drink_by_year: item.drink_by_year ?? '',
      storage_location: item.storage_location || '',
      notes: item.notes || '',
    });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.wine_id) {
      toast.error('Please select a wine');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        wine_id: form.wine_id,
        quantity: parseInt(form.quantity) || 1,
        purchase_date: form.purchase_date || null,
        purchase_price: form.purchase_price !== '' ? parseFloat(form.purchase_price) : null,
        drink_by_year: form.drink_by_year !== '' ? parseInt(form.drink_by_year) : null,
        storage_location: form.storage_location,
        notes: form.notes,
      };
      if (editing) {
        await updateCellarItem(editing.id, payload);
        toast.success('Updated');
      } else {
        await addToCellar(payload);
        toast.success('Added to your cellar');
      }
      setShowForm(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Remove ${item.wine_name} from your cellar?`)) return;
    try {
      await deleteCellarItem(item.id);
      toast.success('Removed from cellar');
      load();
    } catch (e) {
      toast.error('Could not delete');
    }
  };

  const currentYear = new Date().getFullYear();
  const sortedItems = useMemo(
    () => [...items].sort((a, b) => (b.drink_by_year || 9999) - (a.drink_by_year || 9999)),
    [items]
  );

  if (loading || authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-[#d4af37]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-8 pb-20" data-testid="cellar-page">
      <div className="container mx-auto px-4 md:px-8">
        <div className="mb-10 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-[#d4af37] mb-2 flex items-center gap-2">
              <Layers className="w-3 h-3" /> Personal Inventory
            </p>
            <h1 className="text-4xl md:text-5xl font-serif text-stone-50">My Cellar</h1>
            <p className="text-stone-500 mt-2 text-sm">
              Track every bottle you own — vintages, storage, pricing, and when to drink them.
            </p>
          </div>
          <Button
            onClick={openAdd}
            className="bg-[#d4af37] text-stone-950 hover:bg-[#b5952f] rounded-none uppercase tracking-widest text-xs px-6"
            data-testid="add-to-cellar-btn"
          >
            <Plus className="w-4 h-4 mr-2" /> Add Bottle
          </Button>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-10">
            <StatCard icon={Package} label="Bottles" value={stats.total_bottles} />
            <StatCard icon={Wine} label="Unique Wines" value={stats.unique_wines} />
            <StatCard
              icon={DollarSign}
              label="Est. Value"
              value={`$${stats.total_value.toFixed(0)}`}
              accent="#14b8a6"
            />
            <StatCard
              icon={DollarSign}
              label="Avg / Bottle"
              value={stats.avg_price_per_bottle ? `$${stats.avg_price_per_bottle.toFixed(2)}` : '—'}
              accent="#d4af37"
            />
            <StatCard
              icon={AlarmClock}
              label="Drink Soon"
              value={stats.drink_soon}
              accent="#9f1239"
            />
          </div>
        )}

        {/* Items List */}
        {items.length === 0 ? (
          <div className="text-center py-20 bg-stone-900/20 border border-white/5" data-testid="cellar-empty">
            <Wine className="w-16 h-16 text-stone-700 mx-auto mb-6" />
            <h3 className="text-2xl font-serif text-stone-400 mb-2">Your Cellar is Empty</h3>
            <p className="text-stone-600 mb-6">Add your first bottle to start tracking your collection.</p>
            <Button
              onClick={openAdd}
              className="bg-[#d4af37] text-stone-950 hover:bg-[#b5952f] rounded-none uppercase tracking-widest text-xs"
            >
              <Plus className="w-4 h-4 mr-2" /> Add First Bottle
            </Button>
          </div>
        ) : (
          <div className="overflow-x-auto border border-white/5" data-testid="cellar-table">
            <table className="w-full min-w-[720px]">
              <thead>
                <tr className="bg-stone-900/60 border-b border-white/10">
                  <th className="text-left px-4 py-3 text-xs uppercase tracking-widest text-stone-500">Wine</th>
                  <th className="text-center px-4 py-3 text-xs uppercase tracking-widest text-stone-500">Qty</th>
                  <th className="text-left px-4 py-3 text-xs uppercase tracking-widest text-stone-500">Drink By</th>
                  <th className="text-left px-4 py-3 text-xs uppercase tracking-widest text-stone-500">Location</th>
                  <th className="text-right px-4 py-3 text-xs uppercase tracking-widest text-stone-500">Price</th>
                  <th className="text-right px-4 py-3 text-xs uppercase tracking-widest text-stone-500">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedItems.map((it) => {
                  const urgent = it.drink_by_year && it.drink_by_year <= currentYear + 1;
                  return (
                    <tr
                      key={it.id}
                      className="border-b border-white/5 hover:bg-stone-900/30 transition-colors"
                      data-testid={`cellar-row-${it.id}`}
                    >
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-[#d4af37] mb-1">
                          <Grape className="w-2.5 h-2.5" />
                          {it.grape_variety}
                        </div>
                        <Link
                          to={`/wines/${it.wine_id}`}
                          className="text-stone-50 font-serif hover:text-[#d4af37] transition-colors"
                        >
                          {it.wine_name}
                          {it.wine_vintage && (
                            <span className="text-stone-500 text-sm ml-2">{it.wine_vintage}</span>
                          )}
                        </Link>
                        {it.vineyard_name && (
                          <p className="text-xs text-stone-500 flex items-center gap-1 mt-0.5">
                            <MapPin className="w-3 h-3" /> {it.vineyard_name}
                          </p>
                        )}
                        {it.notes && (
                          <p className="text-xs text-stone-600 mt-1 italic line-clamp-1">{it.notes}</p>
                        )}
                      </td>
                      <td className="px-4 py-4 text-center text-stone-300">{it.quantity}</td>
                      <td className="px-4 py-4">
                        {it.drink_by_year ? (
                          <span
                            className={`text-sm ${urgent ? 'text-rose-400' : 'text-stone-400'}`}
                          >
                            {it.drink_by_year}
                            {urgent && (
                              <span className="block text-[10px] uppercase tracking-widest">Soon!</span>
                            )}
                          </span>
                        ) : (
                          <span className="text-stone-600">—</span>
                        )}
                      </td>
                      <td className="px-4 py-4 text-sm text-stone-400">
                        {it.storage_location || <span className="text-stone-600">—</span>}
                      </td>
                      <td className="px-4 py-4 text-right text-sm text-stone-400">
                        {it.purchase_price != null ? `$${it.purchase_price.toFixed(2)}` : '—'}
                      </td>
                      <td className="px-4 py-4 text-right">
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => openEdit(it)}
                          className="text-stone-500 hover:text-[#d4af37] h-8 w-8"
                          data-testid={`edit-cellar-${it.id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => handleDelete(it)}
                          className="text-stone-500 hover:text-red-400 h-8 w-8"
                          data-testid={`delete-cellar-${it.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="bg-stone-900 border-stone-700 max-w-lg" data-testid="cellar-form-dialog">
          <DialogHeader>
            <DialogTitle className="text-stone-50 font-serif">
              {editing ? 'Edit Bottle' : 'Add Bottle to Cellar'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-stone-400">Wine *</Label>
              <Select
                value={form.wine_id}
                onValueChange={(v) => setForm((f) => ({ ...f, wine_id: v }))}
              >
                <SelectTrigger className="bg-stone-900/50 border-stone-700" data-testid="cellar-wine-select">
                  <SelectValue placeholder="Select a wine" />
                </SelectTrigger>
                <SelectContent className="bg-stone-900 border-stone-700 max-h-72">
                  {wines.map((w) => (
                    <SelectItem key={w.id} value={w.id}>
                      {w.name} {w.vintage ? `(${w.vintage})` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-stone-400">Quantity *</Label>
                <Input
                  type="number"
                  min="1"
                  value={form.quantity}
                  onChange={(e) => setForm((f) => ({ ...f, quantity: e.target.value }))}
                  className="bg-stone-900/50 border-stone-700"
                  data-testid="cellar-quantity-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-stone-400">Drink By Year</Label>
                <Input
                  type="number"
                  min="1900"
                  max="2100"
                  placeholder="e.g. 2030"
                  value={form.drink_by_year}
                  onChange={(e) => setForm((f) => ({ ...f, drink_by_year: e.target.value }))}
                  className="bg-stone-900/50 border-stone-700"
                  data-testid="cellar-drink-by-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-stone-400">Purchase Date</Label>
                <Input
                  type="date"
                  value={form.purchase_date}
                  onChange={(e) => setForm((f) => ({ ...f, purchase_date: e.target.value }))}
                  className="bg-stone-900/50 border-stone-700"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-stone-400">Purchase Price</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="e.g. 45.00"
                  value={form.purchase_price}
                  onChange={(e) => setForm((f) => ({ ...f, purchase_price: e.target.value }))}
                  className="bg-stone-900/50 border-stone-700"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-stone-400">Storage Location</Label>
              <Input
                placeholder="e.g. Basement rack B3"
                value={form.storage_location}
                onChange={(e) => setForm((f) => ({ ...f, storage_location: e.target.value }))}
                className="bg-stone-900/50 border-stone-700"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-stone-400">Notes</Label>
              <Textarea
                rows={2}
                value={form.notes}
                onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                className="bg-stone-900/50 border-stone-700"
              />
            </div>
            <div className="flex gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowForm(false)}
                className="flex-1 border-stone-700 rounded-none"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={saving}
                className="flex-1 bg-[#d4af37] text-stone-950 hover:bg-[#b5952f] rounded-none uppercase tracking-widest text-xs"
                data-testid="cellar-submit-btn"
              >
                {saving ? 'Saving…' : editing ? 'Save Changes' : 'Add to Cellar'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Cellar;
